<template>
  <div class="container is-max-desktop mt-4">
    <div class="notification is-warning">
      <h2 class="title">DEBUGGING</h2>
      
      <!-- Notification Debug Section -->
      <div class="box">
        <h3 class="title is-5">Notifications</h3>
        <div class="buttons">
          <button 
            class="button is-primary" 
            @click="sendTestNotification"
          >
            Send Test Notification
          </button>
          <button 
            class="button is-info" 
            @click="testTimerStartNotification"
          >
            Test Timer Start
          </button>
          <button 
            class="button is-success" 
            @click="testTimerEndNotification"
          >
            Test Timer End
          </button>
          <button 
            class="button is-warning" 
            @click="requestNotificationPermission"
            v-if="notificationPermission !== 'granted'"
          >
            Request Permission
          </button>
        </div>
      </div>

      <!-- Store Debug Section -->
      <div class="box">
        <h3 class="title is-5">Store State</h3>
        <pre><code>{{ debugStore }}</code></pre>
      </div>

      <!-- Controls Section -->
      <div class="box">
        <h3 class="title is-5">Controls</h3>
        <div class="buttons">
          <button class="button" @click="store.renewSession">Renew Session</button>
          <button class="button" @click="store.isFrontendOnly = !store.isFrontendOnly">
            {{ store.isFrontendOnly ? 'Switch to Backend Mode' : 'Switch to Local Mode' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useBlocksStore } from '../stores/blocks';
import { notificationService } from '../utils/notificationService.js';

// Props to receive data from parent
const props = defineProps({
  displayStart: {
    type: Date,
    required: true
  }
});

const store = useBlocksStore();

// Reactive state for browser info
const documentHasFocus = ref(document.hasFocus());
const documentVisibility = ref(document.visibilityState);

// Update focus and visibility state
const updateDocumentState = () => {
  documentHasFocus.value = document.hasFocus();
  documentVisibility.value = document.visibilityState;
};

onMounted(() => {
  window.addEventListener('focus', updateDocumentState);
  window.addEventListener('blur', updateDocumentState);
  document.addEventListener('visibilitychange', updateDocumentState);
});

onUnmounted(() => {
  window.removeEventListener('focus', updateDocumentState);
  window.removeEventListener('blur', updateDocumentState);
  document.removeEventListener('visibilitychange', updateDocumentState);
});

// Browser detection
const browserInfo = computed(() => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Unknown';
});

// Notification debug computed properties and methods
const notificationSupported = computed(() => notificationService.supported);
const notificationPermission = computed(() => notificationService.currentPermission);
const canShowService = computed(() => notificationService.canShow);
const userNotificationsEnabled = computed(() => store.getUserSetting('notifications_enabled', false));
const canShowNotifications = computed(() => store.shouldShowNotifications());

const sendTestNotification = () => {
  console.log('Sending test notification:', {
    supported: notificationService.supported,
    permission: notificationService.currentPermission,
    canShow: notificationService.canShow
  });
  
  const result = notificationService.show('Test Notification', {
    body: 'This is a test notification from Timist Blocks!',
    icon: '/icon.png'
  });
  
  console.log('Notification result:', result);
  
  // Add event listeners to debug what's happening
  if (result) {
    result.onshow = () => console.log('Notification shown successfully');
    result.onclick = () => console.log('Notification clicked');
    result.onclose = () => console.log('Notification closed');
    result.onerror = (error) => console.error('Notification error:', error);
  }
};

const testTimerStartNotification = () => {
  store.showTimerDueNotification({ title: 'Test Timer', id: 'test-timer-1' });
};

const testTimerEndNotification = () => {
  store.showTimerCompletionNotification({ title: 'Test Timer', id: 'test-timer-2' });
};

const requestNotificationPermission = async () => {
  try {
    await store.enableNotifications();
  } catch (error) {
    console.error('Error requesting notification permission:', error);
  }
};

// Debug store computed property
const debugStore = computed(() => ({
  blocks: store.blocks,
  events: store.events, // Raw events from API
  allEvents: store.allEvents, // Combined events including active ones
  userSession: store.userSession,
  isFrontendOnly: store.isFrontendOnly,
  currentTime: store.currentTime,
  startTime: store.startTime,
  displayStart: props.displayStart,
}));
</script>
